import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetail({ news }: { news: NewsItem }) {
  usePageMeta(`${news.title} - 蜜月TV`, news.summary);

  const related = useMemo(
    () => newsList.filter((item) => item.id !== news.id).slice(0, 3),
    [news.id],
  );

  const paragraphs = news.content.split("\n").filter((p) => p.trim().length > 0);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: news.category },
        ]}
      />

      <article className="max-w-3xl mx-auto mt-5">
        <span className="inline-block px-3 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium mb-4">
          {news.category}
        </span>
        <h1 className="text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
          {news.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-foreground-500 mt-4">
          <span className="flex items-center gap-1.5"><i className="ri-calendar-line" /> {news.date}</span>
          <span className="flex items-center gap-1.5"><i className="ri-user-line" /> 蜜月TV编辑部</span>
        </div>

        <div className="w-full aspect-[16/9] rounded-xl overflow-hidden bg-background-200 mt-6">
          <img
            src={news.cover}
            alt={`${news.title} 资讯配图`}
            title={`${news.title} - 蜜月TV影视资讯`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="mt-7 space-y-5">
          {paragraphs.map((paragraph, index) => (
            <p key={index} className="text-sm md:text-base text-foreground-700 leading-8">
              {paragraph}
            </p>
          ))}
        </div>
      </article>

      <section className="max-w-3xl mx-auto mt-12">
        <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <span className="w-1 h-5 rounded-full bg-primary-500" />
          相关阅读
        </h2>
        <div className="space-y-3">
          {related.map((item) => (
            <Link
              key={item.id}
              to={`/detail/${item.id}`}
              className="group flex items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="shrink-0 w-24 h-16 rounded-md overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 配图`}
                  title={`${item.title} - 蜜月TV影视资讯`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                  {item.title}
                </h3>
                <span className="text-xs text-foreground-400 mt-1 block">{item.date}</span>
              </div>
            </Link>
          ))}
        </div>
        <Link
          to="/news"
          className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" /> 返回资讯列表
        </Link>
      </section>
    </div>
  );
}